import { describeApiError, type SalaryModel, type SalaryModelVersion } from '@brush/api-client';
import {
  ErrorNotice,
  ErrorRetry,
  PlatformBadge,
  SalaryModelBuilder,
  SalaryPoolPreviewPane,
  Spinner,
  VersionStatusPill,
  readSalaryDefinition,
  readSalaryPreviewScope,
  salaryDefinitionKey,
  salaryPreviewScopeKey,
  toSalaryDocument,
  toSalaryPreviewScopeDocument,
  toSalaryPreviewState,
  unsavedSalaryLabel,
  useDebouncedValue,
  validateSalaryDefinition,
  type SalaryDefinition,
  type SalaryPreviewScope,
} from '@brush/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  useCreateSalaryModelVersion,
  useDeleteSalaryModelVersion,
  useLeaderboards,
  usePatchSalaryModel,
  usePublishSalaryModelVersion,
  useSalaryModelVersion,
  useSalaryPreview,
  useSaveSalaryModelVersion,
} from '../../queries';
import { platformTestScopeNotice } from '../../catalogue/platformScope';
import { useRoom } from '../../room';
import { useUnsavedWork } from '../../unsaved';
import { Modal, btnGhost, btnPrimary, modalControl } from '../Modal';

/**
 * The designer, for one of this room's salary models — or a read-only look at one of the
 * platform's.
 *
 * The version picker, the empty-model state, the draft-versus-stored dirty check, the publish
 * confirmation and the way out all match the points system builder exactly (§4.1), because an
 * operator who has learned that screen should not have to learn this one. What differs is what a
 * version *says*, which is §4.2's document, and that is the shared builder.
 *
 * The pool needs no room selector: a room prices from its own history and nothing else, which the
 * server enforces in the lookback resolver rather than trusting the client.
 */
export function SalaryModelBuilderScreen({
  model,
  onBack,
  onDiscarded,
  onCloneRequest,
}: {
  model: SalaryModel;
  /** Routed through the unsaved-work guard by the caller, like every other way out of an editor. */
  onBack: () => void;
  /**
   * Leaving after the draft has been deleted. Separate from `onBack` on purpose: it must *not* go
   * through the unsaved-work guard, since asking "leave without saving?" about a draft the
   * operator has just deliberately thrown away is nonsense.
   */
  onDiscarded: () => void;
  /** Offered instead of editing on a platform model (§4.1). */
  onCloneRequest?: () => void;
}) {
  const versions = [...model.versions].sort((a, b) => b.versionNumber - a.versionNumber);
  const newest = versions[0]?.versionNumber ?? null;
  const [versionNumber, setVersionNumber] = useState<number | null>(newest);

  // Only fills in a missing selection. It deliberately does not follow the newest version: an
  // operator who chose v1 to read it would otherwise be snapped back to v3 on the next refetch.
  useEffect(() => {
    if (versionNumber === null && newest !== null) setVersionNumber(newest);
  }, [newest, versionNumber]);

  const { roomId } = useRoom();
  const version = useSalaryModelVersion(model.id, versionNumber);
  const createVersion = useCreateSalaryModelVersion(roomId);

  const known = versions.map((v) => ({ versionNumber: v.versionNumber, status: v.status }));
  // A version created a moment ago is edited before the catalogue has refetched, so the selected
  // number can legitimately be missing from the list. Without this the select renders blank,
  // which reads as having lost the new draft.
  const options =
    versionNumber !== null && !known.some((v) => v.versionNumber === versionNumber)
      ? [{ versionNumber, status: 'draft' }, ...known]
      : known;
  const status = options.find((v) => v.versionNumber === versionNumber)?.status ?? 'draft';

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/55 transition hover:text-ink-950"
      >
        ← All salary models
      </button>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        {/* An `h2`: the page is Settings, and this is a screen within one of its panes. */}
        <h2 className="flex flex-wrap items-center gap-2 font-display text-xl font-semibold text-ink-950">
          {model.isPlatform ? <PlatformBadge /> : null}
          {model.name}
          {/* Beside the name, because "can I change this" is the first thing anybody opening this
              screen wants to know. */}
          {versionNumber !== null ? <VersionStatusPill status={status} /> : null}
        </h2>

        {options.length > 0 && versionNumber !== null ? (
          <div className="flex items-center gap-2">
            <label
              className="text-[11px] uppercase tracking-[0.08em] text-ink-800/45"
              htmlFor="salary-version"
            >
              Version
            </label>
            <select
              id="salary-version"
              value={versionNumber}
              onChange={(e) => setVersionNumber(Number(e.target.value))}
              className={modalControl}
            >
              {options.map((v) => (
                <option key={v.versionNumber} value={v.versionNumber}>
                  v{v.versionNumber} — {v.status}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {versionNumber === null ? (
        <EmptyModel
          model={model}
          pending={createVersion.isPending}
          error={describeApiError(createVersion.error)}
          onCreate={
            model.isPlatform
              ? null
              : () =>
                  createVersion.mutate(
                    { id: model.id, copyFromVersion: null },
                    { onSuccess: (created) => setVersionNumber(created.versionNumber) },
                  )
          }
        />
      ) : version.isLoading ? (
        <Spinner label="Loading the model…" />
      ) : version.isError || !version.data ? (
        <ErrorRetry message="Couldn't load that version." onRetry={() => void version.refetch()} />
      ) : (
        <Editor
          // Remounted per version, so switching between a published version and a draft never
          // carries one's edits into the other.
          key={`${model.id}:${versionNumber}`}
          model={model}
          version={version.data}
          onVersionCreated={setVersionNumber}
          onVersionDiscarded={onDiscarded}
          onCloneRequest={onCloneRequest}
        />
      )}
    </div>
  );
}

/** A catalogue entry with nothing in it: no contest can pin it, because there is nothing to pin. */
function EmptyModel({
  model,
  onCreate,
  pending,
  error,
}: {
  model: SalaryModel;
  onCreate: (() => void) | null;
  pending: boolean;
  error: string | null;
}) {
  return (
    <div className="border border-line bg-surface p-10 text-center">
      <p className="text-sm font-medium text-ink-950">{model.name} has no versions yet</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-ink-800/60">
        A model is a catalogue entry; what actually prices players is a version of it. Nothing can
        pin this until it has one.
      </p>
      {onCreate ? (
        /* Not `btnPrimary`: that carries `flex-1` for a dialog footer, and a second width utility
           here would resolve by stylesheet order rather than class order. */
        <button
          type="button"
          onClick={onCreate}
          disabled={pending}
          className="mt-4 bg-felt-700 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-felt-900 disabled:opacity-50"
        >
          {pending ? 'Creating…' : 'Start version 1'}
        </button>
      ) : null}
      <ErrorNotice message={error} className="mt-4 text-left" />
    </div>
  );
}

function Editor({
  model,
  version,
  onVersionCreated,
  onVersionDiscarded,
  onCloneRequest,
}: {
  model: SalaryModel;
  version: SalaryModelVersion;
  onVersionCreated: (n: number) => void;
  /**
   * Called once a draft has been discarded. It leaves the screen rather than picking another
   * version: the catalogue has not refetched yet, so the newest version this screen knows about is
   * the one just deleted, and selecting it would show a model that no longer exists as though it
   * were still editable.
   */
  onVersionDiscarded: () => void;
  onCloneRequest?: () => void;
}) {
  const { room, roomId } = useRoom();

  // Two separate reasons this can be read-only, and they are told apart on screen: a platform
  // model belongs to somebody else (§4.1), a published version belongs to the past.
  const editable = !model.isPlatform && version.status === 'draft';

  // The preview settings answer only to the first of those. Publishing freezes the *definition*
  // and explicitly not the test lookback — it is a column of its own that the engine never
  // receives, so changing it cannot reach a contest or the frozen document. The same boundary the
  // points designer's §9.6 draws, and the same trap it warns about: wiring this pane to the
  // version's status would make an already-published model untestable, which is a normal thing to
  // want to do. What does close it is ownership — a room may not write to a platform model at all,
  // and an editable control with nowhere to save to loses the operator's change.
  const scopeEditable = !model.isPlatform;

  const [draft, setDraft] = useState<SalaryDefinition>(() =>
    readSalaryDefinition(version.definition),
  );
  const [scope, setScope] = useState<SalaryPreviewScope>(
    () => readSalaryPreviewScope(version.previewScope) ?? { leaderboardIds: [] },
  );

  // What "unchanged" means: whatever was last stored. Updated on a successful save, so the footer
  // goes quiet without anything having to remember that a save happened.
  const [baseline, setBaseline] = useState(() => salaryDefinitionKey(draft));
  const [scopeBaseline, setScopeBaseline] = useState(() => salaryPreviewScopeKey(scope));
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Tracked separately, because the footer has to say which one moved: a screen that answers a
  // changed preview setting with "Unsaved changes" says the opposite of what the pane says about
  // testing not touching the model. It is also why Publish is gated on the definition alone.
  const definitionDirty = editable && salaryDefinitionKey(draft) !== baseline;
  const scopeDirty = scopeEditable && salaryPreviewScopeKey(scope) !== scopeBaseline;
  const dirty = definitionDirty || scopeDirty;

  // The section nav, the room switcher, the sibling Settings panes and the back button all route
  // through the same guard, and `beforeunload` covers closing the tab. Only the component holding
  // the draft has to say so.
  useUnsavedWork(dirty);

  const validation = useMemo(() => validateSalaryDefinition(draft), [draft]);

  const save = useSaveSalaryModelVersion(roomId);
  const patchModel = usePatchSalaryModel(roomId);
  const publish = usePublishSalaryModelVersion(roomId);
  const createVersion = useCreateSalaryModelVersion(roomId);
  const deleteVersion = useDeleteSalaryModelVersion(roomId);

  // Which past series the lookback may name. The server filters to this room and drops a foreign
  // board with a warning rather than refusing, so this list is a convenience and not the control.
  const leaderboards = useLeaderboards(roomId);
  const boards = useMemo(
    () =>
      (leaderboards.data ?? []).map((board) => ({
        id: board.id,
        name: board.name,
        status: board.status,
      })),
    [leaderboards.data],
  );

  // The two documents the preview takes, debounced together so a band edit and a lookback change
  // cost one request rather than two. Long enough that dragging a spinner does not spend the
  // endpoint's per-minute allowance — which it *shares* with the points preview — and short enough
  // that the pool still feels attached to what is being typed.
  const request = useMemo(
    () => ({
      definition: toSalaryDocument(draft),
      leaderboardIds: [...scope.leaderboardIds],
    }),
    [draft, scope],
  );
  const settled = useDebouncedValue(request, 800);

  const preview = useSalaryPreview({
    definition: settled.definition,
    leaderboardIds: settled.leaderboardIds,
    roomId,
    enabled: validation.isValid,
  });

  const previewState = toSalaryPreviewState({
    blocked: !validation.isValid,
    idleMessage: null,
    data: preview.data,
    isFetching: preview.isFetching,
    error: preview.isError ? describeApiError(preview.error) : null,
  });

  const submitSave = () => {
    save.mutate(
      {
        id: model.id,
        n: version.versionNumber,
        // A PUT carries the whole version, so the definition rides along even when only the
        // preview settings moved. On a frozen version it goes back **exactly as it was read**
        // rather than as `toSalaryDocument(draft)` — that is this build's shape of a document
        // stored verbatim, and a published one may carry keys this build knows nothing about. The
        // server compares and refuses a changed document, which is the right answer to a
        // definition this screen had quietly rewritten on the way through.
        definition: editable ? toSalaryDocument(draft) : version.definition,
        previewScope: toSalaryPreviewScopeDocument(scope),
      },
      {
        onSuccess: () => {
          setBaseline(salaryDefinitionKey(draft));
          setScopeBaseline(salaryPreviewScopeKey(scope));
          // The catalogue row and the definition both carry a name, and they are written together
          // so the list never shows one thing while the document says another. Only while the
          // definition is editable: on a frozen version the name fields are read-only, so any
          // disagreement is one that was already stored and is not this save's to fix.
          if (
            editable &&
            (draft.name.trim() !== model.name ||
              (draft.description ?? '') !== (model.description ?? ''))
          ) {
            patchModel.mutate({
              id: model.id,
              name: draft.name.trim(),
              description: draft.description ?? '',
            });
          }
        },
      },
    );
  };

  const actionError =
    describeApiError(save.error) ??
    describeApiError(publish.error) ??
    describeApiError(createVersion.error) ??
    describeApiError(deleteVersion.error);

  const unsaved = unsavedSalaryLabel({ definition: definitionDirty, scope: scopeDirty });
  const topBand = draft.mapping.bands[draft.mapping.bands.length - 1];

  return (
    <>
      <SalaryModelBuilder
        value={draft}
        onChange={setDraft}
        validation={validation}
        readOnly={!editable}
        leaderboards={boards}
        readOnlyNotice={
          model.isPlatform ? (
            <>
              <strong>This model belongs to the platform.</strong> Every room can price with it as
              it stands, and no room can change it. Take a copy to make one that is yours.
              {/* The API withholds a platform version's test scope from anybody but an
                  administrator (#583), so the preview opens with no boards chosen. */}
              {platformTestScopeNotice(model, version) ? ` ${platformTestScopeNotice(model, version)}` : null}
            </>
          ) : version.status === 'published' ? (
            <>
              <strong>
                Version {version.versionNumber} is published, so this model is frozen.
              </strong>{' '}
              Any contest pinned to it charged exactly these prices, so it can never change. Use{' '}
              <em>Edit as new version</em> below to carry it into a fresh draft. Seeing what it
              would charge today is still yours to do — the pool beside it is live.
            </>
          ) : (
            <>
              <strong>Version {version.versionNumber} is archived.</strong> It is kept because a
              contest may have been priced by it, and it is no longer offered to new ones. Its
              prices are frozen; you can still see what it would charge.
            </>
          )
        }
        preview={
          <SalaryPoolPreviewPane
            state={previewState}
            scope={scope}
            onScopeChange={setScope}
            leaderboards={boards}
            topBandFrom={topBand?.fromPercentile ?? null}
            readOnly={!scopeEditable}
            header={
              <p className="text-[11.5px] text-ink-800/55">
                Priced from {room?.name ?? 'this room'}’s own results. A room only ever prices from
                its own players.
              </p>
            }
          />
        }
        // This shell is exactly one viewport tall and `<main>` is the scrolling region, so the
        // preview sticks to the top of that region and is bounded by what is left under the
        // 3.5rem rail — not by the window.
        previewClassName="lg:sticky lg:top-0 lg:max-h-[calc(100dvh-3.5rem)]"
        footer={
          <div className="sticky bottom-0 z-10 -mx-8 mt-6 flex flex-wrap items-center gap-3 border-t border-line bg-ground/95 px-8 py-4 backdrop-blur">
            <span className="mr-auto text-xs">
              {unsaved ? (
                <>
                  {/* Orange means "you must act" in this app, and unsaved work is exactly that —
                      whichever of the two documents it is. Reachable on a frozen version too,
                      where only the preview settings can have moved. */}
                  <span className="font-semibold uppercase tracking-[0.06em] text-signal">
                    {unsaved.label}
                  </span>
                  {unsaved.detail ? (
                    <span className="ml-1.5 text-ink-800/45">{unsaved.detail}</span>
                  ) : null}
                </>
              ) : editable ? (
                <span className="text-ink-800/45">All changes saved</span>
              ) : (
                // Which half is read-only, rather than "read-only" flat: the pool beside it is
                // live, and a footer saying otherwise is what stops anybody trying it.
                <span className="text-ink-800/45">
                  {scopeEditable ? 'Model read-only' : 'Read-only'}
                </span>
              )}
            </span>

            {editable ? (
              <>
                {/* A draft hard-deletes and nothing references it, so this needs an ordinary
                    confirmation and no more — but it does need one, since it sits beside Save. */}
                {confirmDiscard ? (
                  <>
                    <span className="text-xs text-ink-800/70">Discard this draft for good?</span>
                    <button
                      type="button"
                      onClick={() => setConfirmDiscard(false)}
                      className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950"
                    >
                      Keep it
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        deleteVersion.mutate(
                          { id: model.id, n: version.versionNumber },
                          { onSuccess: onVersionDiscarded },
                        )
                      }
                      disabled={deleteVersion.isPending}
                      className="border border-chip-500/40 px-4 py-2 text-sm font-medium text-chip-500 transition hover:bg-chip-500/5 disabled:opacity-40"
                    >
                      {deleteVersion.isPending ? 'Discarding…' : 'Discard'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDiscard(true)}
                    className="border border-chip-500/40 px-4 py-2 text-sm text-chip-500 transition hover:bg-chip-500/5"
                  >
                    Discard this draft
                  </button>
                )}
                <button
                  type="button"
                  onClick={submitSave}
                  disabled={!validation.isValid || !dirty || save.isPending}
                  className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {save.isPending ? 'Saving…' : 'Save draft'}
                </button>
                {/* Gated on the *definition*, not on the preview settings. Publishing freezes the
                    document and the preview settings are not part of it — refusing to publish over
                    an unsaved lookback override would say they were. What it does cost is that
                    edit, and the dialog says so rather than dropping it quietly. */}
                <button
                  type="button"
                  onClick={() => setConfirmPublish(true)}
                  disabled={!validation.isValid || definitionDirty || publish.isPending}
                  title={
                    definitionDirty
                      ? 'Save your changes first — publishing freezes the model as stored.'
                      : undefined
                  }
                  className="bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Publish version {version.versionNumber}
                </button>
              </>
            ) : model.isPlatform ? (
              onCloneRequest ? (
                <button
                  type="button"
                  onClick={onCloneRequest}
                  className="bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900"
                >
                  Clone to edit
                </button>
              ) : null
            ) : (
              <>
                {/* The one write a frozen version still takes: the preview settings beside the
                    definition, sent back untouched. */}
                {scopeEditable ? (
                  <button
                    type="button"
                    onClick={submitSave}
                    disabled={!scopeDirty || save.isPending}
                    title="Publishing froze the model, not what you try it against."
                    className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {save.isPending ? 'Saving…' : 'Save preview settings'}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    createVersion.mutate(
                      { id: model.id, copyFromVersion: version.versionNumber },
                      { onSuccess: (created) => onVersionCreated(created.versionNumber) },
                    )
                  }
                  disabled={createVersion.isPending}
                  className="bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:opacity-50"
                >
                  {createVersion.isPending ? 'Creating…' : 'Edit as new version'}
                </button>
              </>
            )}
          </div>
        }
      />

      <ErrorNotice message={actionError} className="mt-3" />

      {confirmPublish ? (
        <PublishDialog
          modelName={model.name}
          versionNumber={version.versionNumber}
          unsavedScope={scopeDirty}
          pending={publish.isPending}
          onClose={() => setConfirmPublish(false)}
          onConfirm={() =>
            publish.mutate(
              { id: model.id, n: version.versionNumber },
              { onSuccess: () => setConfirmPublish(false) },
            )
          }
        />
      ) : null}
    </>
  );
}

/**
 * The confirmation says what publishing means, because it cannot be undone.
 *
 * Same four claims the points system's publish dialog makes, in the same order, because the
 * lifecycle is the same one (§4.1) and an operator who has read that dialog should recognise this
 * one. What changes is the noun and what is at stake: a frozen points system decides what somebody
 * scored, a frozen salary model decides what somebody paid.
 */
function PublishDialog({
  modelName,
  versionNumber,
  unsavedScope,
  pending,
  onClose,
  onConfirm,
}: {
  modelName: string;
  versionNumber: number;
  /**
   * The preview settings have changes that were never saved. Not a blocker and not a warning — the
   * bullet it drives says so, because "will this be lost?" is the question at this moment and the
   * answer is no.
   */
  unsavedScope: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      title={`Publish version ${versionNumber}?`}
      subtitle={modelName}
      onClose={onClose}
      width="max-w-lg"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Not yet
          </button>
          <button type="button" onClick={onConfirm} disabled={pending} className={btnPrimary}>
            {pending ? 'Publishing…' : 'Publish'}
          </button>
        </>
      }
    >
      <ul className="space-y-2.5 text-sm leading-relaxed text-ink-800/80">
        <li>
          <strong className="text-ink-950">This model freezes.</strong> A published model can never
          be edited — not by you, not by anybody. An entrant asking in two years why a player cost
          what they cost gets this exact document as the answer.
        </li>
        <li>
          <strong className="text-ink-950">Contests already open are unaffected.</strong> A contest
          works out its pool and freezes every price when it publishes, so publishing here cannot
          move a salary anybody has already drafted against.
        </li>
        <li>
          <strong className="text-ink-950">New contests can use it.</strong> Only published versions
          appear when a contest picks a salary model; drafts never do.
        </li>
        <li>
          To change it afterwards, use <em>Edit as new version</em> — that copies this into a fresh
          draft and leaves the published one exactly as it is.
        </li>
        {unsavedScope ? (
          <li>
            <strong className="text-ink-950">Your preview settings are not part of this.</strong>{' '}
            They have unsaved changes, and they are safe: what you try the model against is stored
            beside it rather than inside it, and stays editable after publishing. Save them now or
            afterwards.
          </li>
        ) : null}
      </ul>
    </Modal>
  );
}
